import { components } from '@invo.space/shared'
import { useTranslation } from 'react-i18next'
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ChartConfig, ChartContainer } from '@/components/ui/chart'
import { formatCurrency } from '@/lib/formats/formatCurrency'
import { formatDate } from '@/lib/formats/formatDate'

type SkuPrice = components['schemas']['SkuPrice']

const chartConfig = {
  price: {
    color: 'hsl(var(--chart-1))',
  },
} satisfies ChartConfig

interface PriceTimelineChartProps {
  prices: SkuPrice[]
}

export const PriceTimelineChart = ({ prices }: PriceTimelineChartProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog.products.detail.priceHistory',
  })

  const sorted = [...prices].sort(
    (a, b) => new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime(),
  )
  const currency = sorted[0]?.price.currency
  const points = sorted.map((price) => ({
    time: new Date(price.validFrom).getTime(),
    price: Number(price.price.amount) / 100,
  }))
  const last = sorted.at(-1)

  // Extend the last price to its end (or to today) so the step line shows how long it applies.
  if (last) {
    points.push({
      time: last.validTo
        ? new Date(last.validTo).getTime()
        : Math.max(Date.now(), points.at(-1)!.time),
      price: points.at(-1)!.price,
    })
  }

  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-56 w-full">
      <LineChart data={points}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgb(234, 236, 244)" />
        <XAxis
          dataKey="time"
          type="number"
          scale="time"
          domain={['dataMin', 'dataMax']}
          tickFormatter={(value) => formatDate(new Date(value))}
          tick={{ fill: '#6e707e' }}
          tickLine={false}
          fontSize={12}
          minTickGap={24}
        />
        <YAxis
          tickFormatter={(value) => formatCurrency(value, currency) ?? ''}
          tick={{ fill: '#6e707e' }}
          tickLine={false}
          width={80}
          fontSize={12}
          domain={['auto', 'auto']}
        />
        <Tooltip
          content={({ payload }) => {
            const point = payload?.[0]?.payload

            if (!point) return null

            return (
              <Card className="gap-2 py-4">
                <CardHeader>
                  <CardTitle>{formatDate(new Date(point.time))}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex min-w-[130px] items-center gap-4 text-xs text-muted-foreground">
                    <span>{t('table.price')}</span>
                    <div className="ml-auto font-mono font-medium tabular-nums text-foreground">
                      {formatCurrency(point.price, currency)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          }}
        />
        <Line
          type="stepAfter"
          dataKey="price"
          stroke="var(--color-price)"
          dot={{ r: 3, fill: 'var(--color-price)' }}
          strokeWidth={2}
          isAnimationActive={false}
        />
      </LineChart>
    </ChartContainer>
  )
}
